import React, { useEffect, useState } from "react";
import { Card, Typography, Input, Button, Empty, message, Spin } from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "../axiosSetup";
import ProductCodeSelect from "../components/ProductCodeSelect";
import CustomerEntitySelect from "../components/CustomerEntitySelect";
import Breadcrumb from "../components/Breadcrumbs";
import { useNavigate } from "react-router-dom";
import editIcon from "../assets/edit.svg";
import deleteIcon from "../assets/delete.svg";
import { formatMoney } from "../utils/formatMoney";
import { getUser } from "../auth";
import "./MarginCalculator.css";
import { useOverflowTooltip } from "../hooks/useOverflowTooltip";
import CustomTooltip from "../components/CustomTooltip";

const HeaderCell = ({ text }) => {
  const { ref, showTooltip } = useOverflowTooltip(text);

  return (
    <CustomTooltip
      message={text}
      placement="top"
      color="#4A1476"
      show={showTooltip}
    >
      <span
        ref={ref}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        {text}
      </span>
    </CustomTooltip>
  );
};
const ProductCodeCell = ({ text }) => {
  const { ref, showTooltip } = useOverflowTooltip(text);

  return (
    <CustomTooltip
      message={text}
      placement="top"
      color="#4A1476"
      show={showTooltip}
    >
      <span
        ref={ref}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        {text || "-"}
      </span>
    </CustomTooltip>
  );
};


const { Title, Text } = Typography;

const extractApiErrors = (error) => {
  if (!error?.response) {
    return ["Network error. Please check your internet connection."];
  }

  let data = error.response.data;

  if (data?.error) data = data.error;
  if (data?.data) data = data.data;

  const userFriendlyMessage =
    data?.details?.userFriendlyMessage || data?.userFriendlyMessage;

  if (typeof userFriendlyMessage === "string" && userFriendlyMessage.trim()) {
    return [userFriendlyMessage.replace(/\\"/g, '"').trim()];
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return [data.message];
  }

  return ["Something went wrong. Please try again."];
};

const CalculateMargin = () => {
const loggedInUser = getUser();
const isCustomer = loggedInUser?.role?.toLowerCase() === "customer";
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
const [productCodes, setProductCodes] = useState([]);
const [selectedProduct, setSelectedProduct] = useState("");
const [customerError, setCustomerError] = useState("");
const [quantity, setQuantity] = useState("");
const [extraDiscount, setExtraDiscount] = useState("");
const [quantityError, setQuantityError] = useState("");
const [addedProducts, setAddedProducts] = useState([]);
const [editingIndex, setEditingIndex] = useState(null);
const [backendProductError, setBackendProductError] = useState("");
const [quoteName, setQuoteName] = useState("");
const [skipEditRecalcOnce, setSkipEditRecalcOnce] = useState(false);
const [numberOfStores, setNumberOfStores] = useState(1);
const [storeError, setStoreError] = useState("");
const [quoteNameError, setQuoteNameError] = useState("");
const [productError, setProductError] = useState("");
const [backendQuoteNameError, setBackendQuoteNameError] = useState("");
const [bulkError, setBulkError] = useState("");
const [productDetails, setProductDetails] = useState(null); // triggers modal expansion
// const [customerTaxes, setCustomerTaxes] = useState([]);
const [extraDiscountError, setExtraDiscountError] = useState("");
// const JS_MAX_SAFE = 9007199254740991;
const [totals, setTotals] = useState({
  customerPrice: "",
  totalPrice: "",
  grossMargin: "",
  grossMarginPercent: "",
});
const [summary, setSummary] = useState({
  hardwareTotal: 0,
  softwareTotal: 0,
  overallTotal: 0,
  grossMargin: 0,
  grossMarginPercent: 0,
});
const [alert, setAlert] = useState({
  open: false,
  type: "error",
  title: "Error",
  message: "",
});
const navigate = useNavigate();
const [bulkDiscount, setBulkDiscount] = useState(0);
const [isPageLoading, setIsPageLoading] = useState(true);
const customersLoadedRef = React.useRef(false);
const addProductOverlayScrollStyle = {
  alignItems: "center",
  overflow: "hidden",
};
const addProductModalScrollStyle = {
  height: "85vh",
  maxHeight: "85vh",
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
};
const noInnerSectionScrollStyle = { maxHeight: "none", overflowY: "visible" };
const addProductModalScrollbarCss = `
  .modal-overlay .add-product-modal {
    height: 85vh !important;
    max-height: 85vh !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }
  .add-product-modal::-webkit-scrollbar { width: 8px !important; }
  .add-product-modal::-webkit-scrollbar-thumb { background: #b4b4b4; border-radius: 8px; }
  .add-product-modal::-webkit-scrollbar-track { background: transparent; }
`;

const showBackendError = (err) => {
  const backendCode = err?.response?.data?.code;
  let errors = extractApiErrors(err);

  if (backendCode === "UNIQUE_CONSTRAINT_ERROR") {
    errors = ["This quote name already exists. Please enter a different name."];
  }

  setAlert({
    open: true,
    type: "error",
    title: backendCode === "UNIQUE_CONSTRAINT_ERROR" ? "Duplicate Name" : "Error",
    message: errors.join("\n"),
  });
};

  // -------------------------------
  // LOAD CUSTOMERS (ADMIN OR CUSTOMER)
  // -------------------------------
  useEffect(() => {
    if (customersLoadedRef.current) return;
  customersLoadedRef.current = true;
  const loadCustomers = async () => {
    try {
      // Admin: load all customers
      const res = await axios.get("/dropdown/customers");

      setCustomerList(
        res.data.map((c) => ({
          label: c.name,
          value: String(c.id),
          entity: c.entity || c.entityName || c.company || c.organization || c.businessName || "",
          customerCode:c.customerCode,
        }))
      );
    } catch (err) {
      console.error("❌ Failed loading customers", err);
      message.error("Failed loading customers");
    } finally {
      setIsPageLoading(false);
    }
  };

  loadCustomers();
}, []);

useEffect(() => {
  // Always clear products when customer becomes empty
  if (!selectedCustomer) {
    setAddedProducts([]);
    setSummary({
      hardwareTotal: 0,
      softwareTotal: 0,
      overallTotal: 0,
      grossMargin: 0,
      grossMarginPercent: 0,
    });
    setBackendProductError("");
    setShowProductModal(false);
    return;   // ⛔ stop — don't load APIs
  }

  // 🔄 Reset UI when a REAL customer is selected
  setAddedProducts([]);
  setSummary({
    hardwareTotal: 0,
    softwareTotal: 0,
    overallTotal: 0,
    grossMargin: 0,
    grossMarginPercent: 0,
  });
  setBackendProductError("");
  setShowProductModal(false);

  const loadProducts = async () => {
    try {
      const res = await axios.get("/dropdown/product-code", {
        params: { customerId: selectedCustomer },
      });

      setProductCodes(res.data || []);
    } catch (err) {
      console.error("❌ Failed loading product codes", err);
    }
  };

  // Old tax loading logic is intentionally disabled for the new no-tax margin logic.

  loadProducts();
  // loadTaxes();
}, [selectedCustomer]);

const handleProductSelect = async (code) => {
  // Clear user-entered values and old calculated values when product changes.
  setQuantity("");
  setExtraDiscount("");
  setQuantityError("");
  setProductError("");
  setBackendProductError("");
  setTotals({
    customerPrice: "",
    totalPrice: "",
    grossMargin: "",
    grossMarginPercent: "",
  });

  if (!code || !selectedCustomer) {
    setSelectedProduct(code);
    setProductDetails(null);
    return;
  }

  setSelectedProduct(code);
  setProductDetails(null);

  try {
    const res = await axios.get(
      "/dropdown/product-prices-and-customer-discount",
      {
        params: { productCode: code, customerId: selectedCustomer },
      }
    );

    let data = res.data?.data || res.data;

    setProductDetails({
      ...data,
      currency: data.currency || "£",
    });

  } catch (err) {
    console.error("❌ Failed product fetch", err);
    message.error("Failed to load product details");
  }
};

const resetModal = () => {
  setSelectedProduct("");
  setQuantity("");
  setExtraDiscount("");
  setQuantityError("");
  setProductDetails(null);
  setTotals({
    customerPrice: "",
    totalPrice: "",
    grossMargin: "",
    grossMarginPercent: "",
  });
  setEditingIndex(null);
};

useEffect(() => {
  if (!productDetails) return;

  // if (skipEditRecalcOnce) {
  //   setSkipEditRecalcOnce(false);
  //   return;}
  if (!quantity) {
    const { customerPrice } = calculateLineValues(productDetails, 0, extraDiscount);

    setTotals({
      customerPrice: customerPrice.toFixed(2),
      totalPrice: "0.00",
      grossMargin: "0.00",
      grossMarginPercent: "0.00%",
    });

    return;
  }

 if (Number.isNaN(quantity) || quantity < 0) {
    setTotals({
      customerPrice: "",
      totalPrice: "",
      grossMargin: "",
      grossMarginPercent: "",
    });
    return;
  }

  // ✅ CORRECT PRICING LOGIC HERE
  const { customerPrice, totalPrice, grossMargin, grossMarginPercent } =
    calculateLineValues(productDetails, quantity, extraDiscount);

  setTotals({
    customerPrice: customerPrice.toFixed(2),
    totalPrice: totalPrice.toFixed(2),
    grossMargin: grossMargin.toFixed(2),
    grossMarginPercent: grossMarginPercent.toFixed(2) + "%",
  });
}, [productDetails, quantity, extraDiscount]);


const JS_MAX_SAFE = 9007199254740991;

const handleQuantityChange = (value) => {
  let num = Number(value);

  // 🛑 Hard-stop if value exceeds JS safe integer
   if (num <= 0) {
    setQuantity(value);
    setQuantityError("Quantity must be greater than 0");
    return;
  }
  if (num > JS_MAX_SAFE) {
    setQuantityError(`Quantity cannot exceed ${JS_MAX_SAFE}`);
    return;
  }

  // Update field
  setQuantity(value);

  if (!productDetails) return;

  const minQty = productDetails.minimumOrderQuantity;

  //  Negative validation
  if (num < 0) {
    setQuantityError("Quantity cannot be negative");
    return;
  }

  //  Minimum quantity validation
  // if (num > 0 && num < minQty) {
  //   setQuantityError(`Minimum quantity is ${minQty}`);
  //   return;
  // }

  //  Max quantity validation
  if (num > 100000) {
    setQuantityError("Quantity cannot exceed 100000");
    return;
  }

  //  Multiple validation
  if (isCustomer && num > 0 && num % minQty !== 0) {
    setQuantityError(`Quantity must be in multiples of ${minQty}`);
    return;
  }

  // Passed all validations
  setQuantityError("");
};

const cleanPercent = (val) => Number(String(val).replace("%", "").trim()) || 0;

const getBaseCustomerPrice = (product) =>
  Math.max(Number(product?.customerPrice || 0), 0);

const evaluateMarginFormula = (formula, values) => {
  if (!formula || typeof formula !== "string") return 0;

  const normalized = formula.toLowerCase().replace(/\s+/g, "");
  const replacements = {
    rrp: Number(values.rrp || 0),
    landedcost: Number(values.landedCost || 0),
    landcost: Number(values.landedCost || 0),
    costprice: Number(values.costPrice || 0),
    customerprice: Number(values.customerPrice || 0),
  };

  let expression = normalized;
  Object.entries(replacements).forEach(([key, value]) => {
    expression = expression.replace(new RegExp(key, "g"), String(value));
  });

  if (!/^[0-9+\-*/().]+$/.test(expression)) {
    return 0;
  }

  try {
    const result = Function(`"use strict"; return (${expression});`)();
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
};

const calculateLineValues = (product, qty, extraPercent = 0) => {
  const quantityValue = Number(qty) || 0;
  const baseCustomerPrice = getBaseCustomerPrice(product);
  const extra = cleanPercent(extraPercent);
  const finalCustomerPrice = Math.max(
    baseCustomerPrice - (baseCustomerPrice * extra) / 100,
    0
  );

  let grossMarginPerUnit = 0;
  let grossMarginPercent = 0;

  if ((product?.marginType || "").toLowerCase() === "percentage") {
    grossMarginPercent = Number(product?.marginPercentage || 0);
    grossMarginPerUnit = (finalCustomerPrice * grossMarginPercent) / 100;
  } else {
    grossMarginPerUnit = evaluateMarginFormula(product?.marginFormula, {
      rrp: finalCustomerPrice,
      landedCost: Number(product?.landedCost || 0),
      costPrice: Number(product?.costPrice || 0),
      customerPrice: finalCustomerPrice,
    });
    grossMarginPercent =
      finalCustomerPrice > 0
        ? (grossMarginPerUnit * 100) / finalCustomerPrice
        : 0;
  }

  return {
    customerPrice: finalCustomerPrice,
    totalPrice: finalCustomerPrice * quantityValue,
    grossMargin: grossMarginPerUnit * quantityValue,
    grossMarginPercent,
  };
};

const handleSaveProduct = () => {
  // ❌ Prevent adding the same product again (only when adding, not editing)
const isDuplicate = addedProducts.some((p, i) =>
    p.productCode === selectedProduct && i !== editingIndex
  );

  if (isDuplicate) {
    setProductError("This product is already added.");
    return;
  }
  if (!productDetails || !quantity || quantityError) {
    message.error("Please fix quantity before saving");
    return;
  }

 
const {
  customerPrice: beforeCustomerPrice,
  totalPrice: beforeTotal,
  grossMargin: backendBeforeOnTopGrossMargin,
  grossMarginPercent: backendBeforeOnTopGrossMarginPercent,
} = calculateLineValues(productDetails, quantity, 0);

const {
  customerPrice: finalCustomerPrice,
  totalPrice: lineCustomerTotal,
  grossMargin: backendLineGrossMargin,
  grossMarginPercent: backendLineGrossMarginPercent,
} = calculateLineValues(productDetails, quantity, extraDiscount);


  const productData = {
    productType: productDetails.productType,
    productCode: selectedProduct,
    // itemType: productDetails.itemType,
    costPrice: productDetails.costPrice,
    rrp: productDetails.rrp,
    quantity: Number(quantity),
    discount: productDetails.discount,
    extraDiscount,
    // store the final customer price per unit and totals so edit restores correctly
  beforeCustomerPrice: beforeCustomerPrice.toFixed(2),
  beforeTotal: beforeTotal.toFixed(2),
  beforeGrossMargin: backendBeforeOnTopGrossMargin,
  beforeGrossMarginPercent: backendBeforeOnTopGrossMarginPercent,

  // AFTER on-top (your existing logic)
  customerPrice: finalCustomerPrice.toFixed(2),
  totalCustomerPrice: lineCustomerTotal.toFixed(2),
  grossMargin: backendLineGrossMargin,
  grossMarginPercent: backendLineGrossMarginPercent,
    minimumOrderQuantity: productDetails.minimumOrderQuantity,
    marginType: productDetails.marginType,
    marginFormula: productDetails.marginFormula,
    marginPercentage: productDetails.marginPercentage,
    landedCost: productDetails.landedCost,
    currency: productDetails.currency,
    discountType: productDetails.discountType,
  };

  if (editingIndex !== null) {
    const updated = [...addedProducts];
    updated[editingIndex] = productData;
    setAddedProducts(updated);
    setEditingIndex(null);
  } else {
    setAddedProducts((prev) => [...prev, productData]);
  }
  setBackendProductError("");
  resetModal();
  setShowProductModal(false);
};

const handleDeleteProduct = (index) => {
  setAddedProducts(prev => prev.filter((_, i) => i !== index));
  setBackendProductError("");
};
const handleEditProduct = (index) => {
  setBackendProductError("");
  const product = addedProducts[index];

  setEditingIndex(index);
  // setSkipEditRecalcOnce(true);

  // Restore selects/inputs
  setSelectedProduct(product.productCode);
  setQuantity(product.quantity);
  setExtraDiscount(product.extraDiscount || "");

  // Base details (unchanged)
  setProductDetails({
    productType: product.productType,
    // itemType: product.itemType,
    costPrice: product.costPrice,
    rrp: product.rrp,
    discount: product.discount,
    minimumOrderQuantity: product.minimumOrderQuantity,
    marginType: product.marginType,
    marginFormula: product.marginFormula,
    marginPercentage: product.marginPercentage,
    landedCost: product.landedCost,

    // this is still the AFTER price — correct
    customerPrice: Number(product.beforeCustomerPrice ?? product.customerPrice),

    currency: product.currency,
    discountType: product.discountType,
  });

  // ⛔ NO RECALCULATION — use stored values
//   setTotals({
//   customerPrice: Number(
//     product.beforeCustomerPrice ?? product.customerPrice
//   ).toFixed(2),

//   totalPrice: Number(
//     product.beforeTotal ?? product.totalCustomerPrice
//   ).toFixed(2),

//   grossMargin: Number(
//     product.beforeGrossMargin ?? product.grossMargin
//   ).toFixed(2),

//   grossMarginPercent:
//     Number(
//       product.beforeGrossMarginPercent ?? product.grossMarginPercent
//     ).toFixed(2) + "%",
// });


  setShowProductModal(true);
};

useEffect(() => {
  let hardwareTotal = 0;
  let softwareTotal = 0;
  let overallTotal = 0;
  let subtotal = 0;
  let grossMargin = 0;

  // Step 1: Sum all product totals and gross margins (before bulk discount)
  addedProducts.forEach((p) => {
    const total = Number(p.totalCustomerPrice || 0);

    if (p.productType === "Hardware") {
      hardwareTotal += total;
    }
    if (p.productType === "Software") {
      softwareTotal += total;
    }

    overallTotal += total;
    grossMargin += Number(p.grossMargin || 0);
  });

  // Step 2: Apply bulk discount (if any)
  // Formula from backend: 
  // overallTotal = overallTotal - (bulkDiscount * overallTotal / 100)
  // overallGrossMargin = overallGrossMargin - (bulkDiscount * overallGrossMargin / 100)
  
  const bulk = Math.min(Math.max(Number(bulkDiscount) || 0, 0), 100);
  
  if (bulk > 0) {
    const bulkDiscountAmount = (bulk * overallTotal) / 100;
    const bulkDiscountGrossMargin = (bulk * grossMargin) / 100;
    
    // Apply bulk discount to totals and gross margin
    hardwareTotal = hardwareTotal - (bulk * hardwareTotal) / 100;
    softwareTotal = softwareTotal - (bulk * softwareTotal) / 100;
    overallTotal = overallTotal - bulkDiscountAmount;
    grossMargin = grossMargin - bulkDiscountGrossMargin;
  }

  // Step 3: Calculate overall gross margin percentage
  // Formula from backend:
  // overallGrossMarginPercent = overallTotal <= 0 ? 0 : (overallGrossMargin * 100) / overallTotal
  const grossMarginPercent = overallTotal <= 0 ? 0 : (grossMargin * 100) / overallTotal;
  // const overallTotalAfterBulk = overallTotal * discountFactor;

  // Old tax-inclusive margin logic is intentionally disabled for the new logic.
  // const totalTaxPercent = customerTaxes.reduce(
  //   (sum, t) => sum + Number(t.percentage || 0),
  //   0
  // );
  //
  // const taxAmount = (actualCostTotal * totalTaxPercent) / 100;
  // const buyPriceAfterTax = actualCostTotal + taxAmount;
  //
  // Gross margin AFTER tax
  // grossMargin = overallTotal - buyPriceAfterTax;
  //
  // Match backend % behaviour
  // const grossMarginPercent =
  //   grossMargin > 0
  //     ? overallTotal === 0
  //       ? 0
  //       : (grossMargin * 100) / overallTotal
  //     : buyPriceAfterTax === 0
  //       ? 0
  //       : (grossMargin * 100) / buyPriceAfterTax;

  setSummary({
  hardwareTotal,
  softwareTotal,
  overallTotal,
  subtotal,
  grossMargin,
  grossMarginPercent,
});
}, [addedProducts, bulkDiscount]);


const handleFinalSave = async () => {
  if (!selectedCustomer) {
    message.error("Please select a customer");
    return;
  }

  if (addedProducts.length === 0) {
    message.error("Add at least one product");
    return;
  }

  const payload = {
    quoteName: quoteName.trim(),
    customerId: Number(selectedCustomer),
    numberOfStores: Number(numberOfStores) || 1,
    additionalBulkDiscount: Number(bulkDiscount) || 0,

     products: addedProducts.map(p => ({
    productCode: p.productCode,
    quantity: p.quantity,
    onTopStdPercent:
      p.extraDiscount !== "" && p.extraDiscount !== null
        ? Number(p.extraDiscount)
        : null,
  })),

    summary: {
      hardwareTotal: summary.hardwareTotal,
      softwareTotal: summary.softwareTotal,
      overallTotal: summary.overallTotal,
      grossMargin: summary.grossMargin,
      grossMarginPercent: summary.grossMarginPercent,
    },
  };


  try {
    await axios.post("/margin-calculator", payload);

    message.success("Margin saved successfully!");
    setBackendQuoteNameError("");
    navigate("/margin-calculator");
  } catch (err) {
  console.error("❌ Failed to save margin:", err);

  const apiError = err?.response?.data;

  if (apiError?.code === "UNIQUE_CONSTRAINT_ERROR") {
    setBackendQuoteNameError("This quote name already exists.");
    showBackendError(err);
    return;
  }

  // 🔴 Product validation errors (already implemented)
  if (
    apiError?.code === "SEQUELIZE_VALIDATION_ERROR" &&
    Array.isArray(apiError.details)
  ) {
    const quantityError = apiError.details.find(
      (d) => d.path === "quantity"
    );

    if (quantityError) {
      setBackendProductError(quantityError.message);
      return;
    }
  }

  showBackendError(err);
}

};
const summaryCurrency = addedProducts.length > 0 ? addedProducts[0].currency : "£";
const showTopValidation =
  !selectedCustomer ||
  quoteName.trim() === "" ||
  quoteNameError !== "" ||
  backendQuoteNameError !== "";
const loadingIcon = <LoadingOutlined style={{ fontSize: 34, color: "#f50087" }} spin />;

if (isPageLoading) {
  return (
    <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <Spin indicator={loadingIcon} />
        <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
      </div>
    </div>
  );
}

const selectedCustomerName =
  customerList.find(c => String(c.value) === String(selectedCustomer))?.label;

  return (
    <div className="calculate-margin-container">
      {alert.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: 420,
              maxWidth: "90vw",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
              borderLeft: "6px solid #dc2626",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 16px",
                fontWeight: 600,
                fontSize: 16,
                background: "#f9fafb",
              }}
            >
              <span>{alert.title}</span>
              <button
                type="button"
                onClick={() => setAlert((prev) => ({ ...prev, open: false }))}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                padding: "18px 16px",
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.5,
                whiteSpace: "pre-line",
              }}
            >
              {alert.message}
            </div>
            <div
              style={{
                padding: "12px 16px",
                display: "flex",
                justifyContent: "flex-end",
                background: "#f9fafb",
              }}
            >
              <button
                type="button"
                onClick={() => setAlert((prev) => ({ ...prev, open: false }))}
                style={{
                  padding: "8px 18px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  background: "#dc2626",
                  color: "#fff",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {/* <Title level={3}>Calculate Margin</Title>
      <Text type="secondary">Calculate the profit margin for each product</Text> */}
      <Breadcrumb/>


      {/* -------------------- Quote Info -------------------- */}
      <Card className="quote-info-card" bordered={false}>
        {/* <Title level={5} style={{color:"#7509B8"}}>Quote Information</Title> */}

        <div className="quote-info-grid">
          {/* CUSTOMER NAME */}
          <div className="form-field">
            <Text className="label">Customer Name <span className="required-asterisk">*</span></Text>
            <CustomerEntitySelect
              value={selectedCustomer}
              onChange={(v) => {
    setSelectedCustomer(v);

    // clear error once user selects a customer
    if (v) {
      setCustomerError("");
    }
              }}
              placeholder="Select Customer"
              width="100%"
              options={[
                {
                  value: "",
                  customerName: "Select Customer",
                  entityName: "-",
                  searchText: "select customer",
                },
                ...customerList.map((c) => ({
                  value: c.value,
                  customerName: c.label || "-",
                  customerCode:c.customerCode || "-",
                  entityName: c.entityName || c.entity || c.company || c.organization || c.businessName || "-",
                  searchText: `${c.label || ""} ${
                    c.entityName || c.entity || c.company || c.organization || c.businessName || ""
                  } ${c.customerCode||""}`,
                })),
              ]}
            />
            {customerError && (
    <p className="mc-error-text" style={{ color: "red", marginTop: "4px" }}>
      {customerError}
    </p>
  )}
          </div>

          {/* CALCULATION NAME */}
          <div className="form-field">
            <Text className="label">Calculation Name <span className="required-asterisk">*</span></Text>
            <Input 
  placeholder="Enter calculation name"
  value={quoteName}
  style={{height:"38px",
    borderColor: "1px solid #d0d0d0"
  }}
  onChange={(e) => {
  const val = e.target.value;
  setBackendQuoteNameError("");
  if (val.length > 100) {
    setQuoteNameError("Calculation name cannot exceed 100 characters");
  } else if (val.trim() === "") {
    setQuoteNameError("Calculation name is required");
  } else {
    setQuoteNameError("");
  }

  setQuoteName(val);
}}
/>
{quoteNameError && (
  <p className="mc-error-text" style={{
    color:"red",
    marginTop:"4px",
  }}>{quoteNameError}</p>
)}
{backendQuoteNameError && (
  <p
    style={{
      color: "#B91C1C",
      marginTop: "4px",
      fontSize: "14px",
    }}
  >
    {backendQuoteNameError}
  </p>)}
          </div>

          {/* NUMBER OF STORES */}
          <div className="form-field">
            <Text className="label">Number of Stores</Text>
           <Input
  type="number"
  min={1}
  value={numberOfStores}
  style={{ height: "38px",
    borderColor: "1px solid #d0d0d0",
   }}
  onKeyDown={(e) => {
    const invalid = ["-", "+", "e"];
    if (invalid.includes(e.key)) {
      e.preventDefault();
    }
  }}
  onChange={(e) => {
    const val = e.target.value;

    // ✨ allow clearing temporarily
    if (val === "") {
      setNumberOfStores("");
      setStoreError("");
      return;
    }

    const num = Number(val);

    // 🛑 block 0 or negative — do NOT update state
    if (num <= 0) {
      setStoreError("Number of stores must be at least 1");
      return;
    }

    if (num > JS_MAX_SAFE) {
      setStoreError(`Number of stores cannot exceed ${JS_MAX_SAFE}`);
      return;
    }

    if (num > 100000) {
      setStoreError("Number of stores cannot exceed 100,000");
      return;
    }

    // 👍 valid
    setStoreError("");
    setNumberOfStores(val);
  }}
/>

  {storeError && (
    <p className="mc-error-text" style={{ color: "red", marginTop: "4px" }}>
      {storeError}
    </p>
  )}
          </div>

          {/* BULK DISCOUNT */}
          <div className="form-field">
            <Text className="label">Additional Bulk Discount (%)</Text>
            <Input
  type="number"
  min={0}
  max={100}
  style={{height:"38px",
    borderColor: "1px solid #d0d0d0",
  }}
  value={bulkDiscount}
   onChange={(e) => {
    const val = Number(e.target.value);

    if (val < 0 || val > 100) {
      setBulkError("Bulk discount must be between 0 and 100%");
      return;
    }

    setBulkError("");
    setBulkDiscount(val);
  }}
/>

{bulkError && <p className="mc-error-text">{bulkError}</p>}
          </div>
        </div>
      </Card>

      {/* -------------------- Products List -------------------- */}
      <Card className="products-card" bordered={false}>
        <div className="products-header">
          <Title level={5} style={{color:"#7509B8"}}>Products</Title>

          {/* 🔥 ADD PRODUCTS BUTTON DISABLED UNTIL CUSTOMER SELECTED */}
<Button
  type="primary"
  icon={<PlusOutlined />}
  onClick={() => {
    let hasError = false;

    if (!selectedCustomer) {
      setCustomerError("Customer is required");
    hasError = true;
    }

    if (quoteName.trim() === "") {
      setQuoteNameError("Calculation name is required");
      hasError = true;
    }

    if (quoteNameError || backendQuoteNameError) {
      hasError = true;
    }

    if (hasError) return;

    resetModal();
    setBackendProductError("");
    setShowProductModal(true);
  }}
  style={{
    background: "#2b003f",
    borderColor: "#2b003f",
    color: "white",
  }}
>
  Add Products
</Button>

</div>
        <div className="products-content">
{addedProducts.length > 0 && (
  <div className="cm-product-table-wrapper">
  <div className="cm-product-table-scroll">
    <table className="cm-product-table wide-table">
      <thead>
  <tr>
    <th><HeaderCell text="Type of Product" /></th>
    <th><HeaderCell text="Vocovo Product Code" /></th>
    {/* <th><HeaderCell text="Item Type" /></th> */}
    <th><HeaderCell text="Landed Cost" /></th>
    <th><HeaderCell text="RRP" /></th>
    <th><HeaderCell text="Quantity" /></th>
    <th><HeaderCell text="Discounted Price" /></th>
    {/* <th style={{ width: "200px" }}>
      <HeaderCell text="Customer Price" />
    </th> */}
    {/* <th><HeaderCell text="Total Price" /></th> */}
    {/* <th><HeaderCell text="Gross margin after customer discount applied" /></th> */}
    {/* <th><HeaderCell text="Gross margin % after customer discount applied" /></th> */}
    <th><HeaderCell text="On Top of Std Discount %" /></th>
    <th><HeaderCell text="Customer Price" /></th>
    <th><HeaderCell text="Total Price" /></th>
    <th><HeaderCell text="Gross Margin" /></th>
    <th><HeaderCell text="Gross Margin %" /></th>

    <th className="cm-sticky-col">
      <HeaderCell text="Actions" />
    </th>
  </tr>
</thead>


      <tbody>
  {addedProducts.map((p, idx) => (
    <tr key={idx}>
      <td>{p.productType}</td>
      <td> <ProductCodeCell text={p.productCode} /></td>
      {/* <td>{p.itemType}</td> */}
      <td>{formatMoney(p.landedCost, p.currency)}</td>
<td>{formatMoney(p.rrp, p.currency)}</td>
      <td>{p.quantity}</td>

      {/* Customer discount */}
      <td>
        {p.discountType === "Percentage"
          ? `${p.discount}%`
          : `${p.currency}${p.discount}`}
      </td>

      {/* BEFORE — Customer Price */}
     {/* <td>{formatMoney(p.beforeCustomerPrice ?? p.customerPrice, p.currency)}</td> */}
    {/* <td>{formatMoney(p.beforeTotal ?? p.totalCustomerPrice, p.currency)}</td> */}
    {/* <td>{formatMoney(p.beforeGrossMargin ?? p.grossMargin, p.currency)}</td> */}
    {/* <td>{Number(p.beforeGrossMarginPercent ?? p.grossMarginPercent).toFixed(2)}%</td> */}


      {/* EXTRA DISCOUNT (on-top) */}
      <td>{p.extraDiscount || "0"}%</td>

      {/* AFTER — Customer Price */}
      <td>{formatMoney(p.customerPrice, p.currency)}</td>
<td>{formatMoney(p.totalCustomerPrice, p.currency)}</td>
<td>{formatMoney(p.grossMargin, p.currency)}</td>

      {/* AFTER — Gross Margin % */}
      <td>{p.grossMarginPercent.toFixed(2)}%</td>

      {/* STICKY ACTIONS */}
      <td className="cm-sticky-col">
        <img
          src={editIcon}
          alt="edit"
          className="action-icon"
          onClick={() => handleEditProduct(idx)}
        />
        <img
          src={deleteIcon}
          alt="delete"
          className="action-icon"
          onClick={() => handleDeleteProduct(idx)}
        />
      </td>
    </tr>
  ))}
</tbody>

    </table>
  </div>
</div>

)}

        </div>

        {/* EMPTY STATE */}
        {addedProducts.length===0 && (<div className="empty-state">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <>
                <Text strong>No Product Yet</Text>
                <br />
                <Text type="secondary">Add Products to your Calculate</Text>
              </>
            }
          />
        </div>)}
        {backendProductError && (
  <div
    style={{
      marginTop: "12px",
      padding: "8px 12px",
      borderRadius: "6px",
      background: "#FEF2F2",
      border: "1px solid #FCA5A5",
      color: "#B91C1C",
      fontSize: "14px",
    }}
  >
    {backendProductError}
  </div>
)}
      </Card>
{addedProducts.length > 0 && (
  <Card className="overall-summary-card" bordered={false} style={{ marginTop: 24 }}>
    <h3 style={{ color: "#7509B8" }}>Overall Summary</h3>
    {/*
    Old tax display is intentionally disabled for the new no-tax margin logic.
    {customerTaxes.length > 0 && (
  <p style={{ marginTop: 4, color: "#7509B8", fontSize: "13px" }}>
    Taxes applied:&nbsp;
    {customerTaxes.map((t, i) => (
      <span key={i}>
        {t.name} ({t.percentage}%)
        {i < customerTaxes.length - 1 && ", "}
      </span>
    ))}
  </p>
)}

{customerTaxes.length === 0 && (
  <p style={{ marginTop: 4, color: "#6B7280", fontSize: "13px" }}>
    No taxes applied.
  </p>
)}
    */}
    {/* 🔥 Detect currency from first product */}
    {/** Put this above return if needed:
       const summaryCurrency = addedProducts.length > 0 ? addedProducts[0].currency : "£";
    */}

    <div className="mc-summary-grid">

      <div className="summary-card">
        <p>Hardware Total ({summaryCurrency})</p>
        <h4>{formatMoney(summary.hardwareTotal, summaryCurrency)}</h4>
      </div>

      <div className="summary-card">
        <p>Software Total ({summaryCurrency})</p>
        <h4>{formatMoney(summary.softwareTotal, summaryCurrency)}</h4>
      </div>

      <div className="summary-card">
        <p>Overall Total ({summaryCurrency})</p>
        <h4>{formatMoney(summary.overallTotal, summaryCurrency)}</h4>
      </div>

      <div className="summary-card">
        <p>Overall Gross Margin ({summaryCurrency})</p>
        <h4>{formatMoney(summary.grossMargin, summaryCurrency)}</h4>
      </div>

      <div className="summary-card">
        <p>Overall Gross Margin (%)</p>
        <h4>{summary.grossMarginPercent.toFixed(2)}%</h4>
      </div>

    </div>
  </Card>
)}

      {/* BACK BUTTON */}
      {/* ACTION BUTTONS */}
{addedProducts.length > 0 && (
  <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: "12px" }}>
    <Button
      onClick={() => navigate("/margin-calculator")}
      style={{
        border: "1px solid #2D0844",
        color: "#2D0844",
        padding: "8px 24px",
        borderRadius: "8px",
      }}
    >
      Cancel
    </Button>

    <Button
      type="primary"
      style={{
        background: "white",
        // borderColor: "#662D91",
        border: "1px solid #2D0844",
        color: "#2D0844",
        padding: "8px 24px",
        borderRadius: "8px",
        
      }}
      onClick={handleFinalSave}
    >
      Save
    </Button>
  </div>
)}

    {showProductModal && (
  <div className="modal-overlay" style={addProductOverlayScrollStyle}>
    <style>{addProductModalScrollbarCss}</style>
    <div className="modal add-product-modal" style={addProductModalScrollStyle}>

      {/* HEADER */}
      <div className="modal-header-addproduct">
        <h3>{editingIndex !== null ? "Edit Product" : "Add Product"}</h3>
        <button className="close-btn" onClick={() => {
  resetModal();
  setShowProductModal(false);
}}>✖</button>
      </div>
      <p className="modal-subtext">{editingIndex !==null ? "Update products details": "Choose product and get it's details"}</p>

      {/* INPUT SECTION */}
      <div className="grid-2" style={noInnerSectionScrollStyle}>

        <div className="field">
          <label>VoCoVo Product Code</label>
          <ProductCodeSelect
            placeholder="Select Product"
            value={selectedProduct}
            width="100%"
            customerName={selectedCustomerName}
            options={productCodes.map((p) => ({
              value: p.productCode,
              productCode: p.productCode,
              productDescription: p.productDescription || p.description || "-",
              productType: p.productType || "-",
              searchText: `${p.productCode || ""} ${p.productDescription || p.description || ""} ${p.productType || ""}`,
            }))}
            onChange={handleProductSelect}
          />
          
  {productError && (
    <p className="mc-error-text" style={{ color: "red", marginTop: "4px" }}>
      {productError}
    </p>)}
        </div>

        <div className="field">
  <label>Quantity</label>
  <input
    type="number"
    value={quantity}
    disabled={!selectedProduct}   
    min={1}     
    onChange={(e) => handleQuantityChange(e.target.value)}
  />

  {quantityError && (
    <p className="mc-error-text">{quantityError}</p>
  )}
</div>


        <div className="field full">
          <label>On Top of Std % (Optional)</label>
          <input
  type="number"
  min={0}
  max={100}
  value={extraDiscount}
  onKeyDown={(e) => {
    const invalid = ["e", "E", "+", "-"];
    if (invalid.includes(e.key)) e.preventDefault();
  }}
  onChange={(e) => {
    const val = e.target.value;

    // allow clearing
    if (val === "") {
      setExtraDiscount("");
      setExtraDiscountError("");
      return;
    }

    const num = Number(val);

    if (num < 0) {
      setExtraDiscount(num);
      setExtraDiscountError("Value cannot be less than 0%");
      return;
    }

    if (num > 100) {
      setExtraDiscount(num);
      setExtraDiscountError("Value cannot be greater than 100%");
      return;
    }

    // valid
    setExtraDiscountError("");
    setExtraDiscount(num);
  }}
/>

{extraDiscountError && (
  <p className="mc-error-text" style={{ color: "red", marginTop: "4px" }}>
    {extraDiscountError}
  </p>
)}

        </div>
      </div>

      {/* EXPANDED SECTION (only when product selected) */}
      {productDetails && (
        <>
        <br/>
          {/* AUTO POPULATED */}
          <h3 style={{ color: "#7509B8" }}>Auto-populated Fields</h3>

          <div className="grid-2" style={noInnerSectionScrollStyle}>
            <div className="field"><label>Type of Product</label><input disabled value={productDetails.productType} /></div>
            {/* <div className="field"><label>Item Type</label><input disabled value={productDetails.itemType} /></div> */}
            <div className="field"><label>Landed Cost ({productDetails?.currency || "£"})</label><input disabled  value={formatMoney(productDetails.landedCost)} /></div>
            <div className="field"><label>RRP ({productDetails?.currency || "£"})</label><input disabled value={formatMoney(productDetails.rrp)} /></div>
            <div className="field">
  <label>
    {productDetails.discountType === "Percentage"
      ? "Discounted Price (%)"
      : `Discounted Price (${productDetails.currency || "£"})`}
  </label>

  <input
    disabled
    value={
      productDetails.discountType === "Percentage"
        ? productDetails.discount 
        : formatMoney(productDetails.discount)
    }
  />
</div>
          </div>
          <br/>
          {/* CALCULATED TOTALS */}
          <h3 style={{ color: "#7509B8" }}>Calculated Totals</h3>

          <div className="grid-2" style={noInnerSectionScrollStyle}>
            <div className="field"><label>Customer Price ({productDetails?.currency || "£"})</label><input disabled value={formatMoney(totals.customerPrice)} /></div>
            <div className="field"><label>Total Price ({productDetails?.currency || "£"})</label><input disabled value={formatMoney(totals.totalPrice)} /></div>
            <div className="field"><label>Gross Margin ({productDetails?.currency || "£"})</label><input disabled value={formatMoney(totals.grossMargin)} /></div>
            <div className="field"><label>Gross Margin (%)</label><input disabled value={totals.grossMarginPercent} /></div>
          </div>

          {/* BUTTONS */}
          <div className="modal-actions-row">
            <button className="cancel-btn" onClick={() => {
    resetModal();
    setShowProductModal(false);
  }}>Cancel</button>
           <button className="submit-btn-cm"  disabled={
    !quantity ||                      // quantity not entered
    quantityError !== "" ||           // invalid quantity (negative, not multiple, etc.)
    !productDetails                  // product not selected
  } onClick={handleSaveProduct}>
  {editingIndex !== null ? "Update" : "Add"}
</button>
          </div>
        </>
      )}

    </div>
  </div>
)}

    </div>
  );
};

export default CalculateMargin;
